import { CollapsibleCard, type ToolRendererProps } from '../ToolCard';

interface HealthEntry {
  name?: string;
  module?: string;
  status?: string;
  healthy?: boolean;
  uptime?: number | string;
}

interface Auction {
  id?: number | string;
  name?: string;
  title?: string;
  status?: string;
  item_count?: number;
  itemCount?: number;
}

function healthDotClass(status: string | undefined, healthy: boolean | undefined): string {
  if (healthy === true || status === 'healthy' || status === 'up') return 'bg-moss';
  if (healthy === false || status === 'down' || status === 'unhealthy') return 'bg-red-500';
  if (status === 'degraded' || status === 'warning') return 'bg-gold';
  return 'bg-bark/40';
}

export function EcosystemCard({ toolName, result }: ToolRendererProps) {
  // Ecosystem health — array of module health entries
  if (Array.isArray(result) && result.length > 0 && typeof result[0] === 'object') {
    const first = result[0] as Record<string, unknown>;
    if ('status' in first || 'healthy' in first) {
      const entries = result as HealthEntry[];
      return (
        <CollapsibleCard toolName={toolName} defaultOpen>
          <ul className="space-y-1">
            {entries.map((e, i) => (
              <li key={(e.name ?? e.module) ?? i} className="flex items-center gap-2 text-xs">
                <span className={`h-2 w-2 shrink-0 rounded-full ${healthDotClass(e.status, e.healthy)}`} />
                <span className="flex-1 font-medium text-espresso">
                  {e.name ?? e.module ?? 'unknown'}
                </span>
                {e.status && <span className="text-[10px] text-bark">{e.status}</span>}
              </li>
            ))}
          </ul>
        </CollapsibleCard>
      );
    }
    // Auction list
    if ('name' in first || 'title' in first) {
      const auctions = result as Auction[];
      return (
        <CollapsibleCard toolName={toolName} defaultOpen>
          <div className="text-[11px] text-bark">{auctions.length} auctions</div>
          <ul className="mt-1 space-y-1">
            {auctions.slice(0, 8).map((a, i) => (
              <li key={(a.id as string | number | undefined) ?? i} className="flex items-start gap-2 text-xs">
                <span className="flex-1 text-espresso">{a.name ?? a.title ?? '—'}</span>
                {(a.item_count ?? a.itemCount) !== undefined && (
                  <span className="shrink-0 text-[10px] text-bark">
                    {a.item_count ?? a.itemCount} items
                  </span>
                )}
                {a.status && (
                  <span className="shrink-0 rounded bg-fog px-1.5 py-0.5 text-[10px] text-bark">
                    {a.status}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </CollapsibleCard>
      );
    }
  }

  // Fallback — pretty-print the payload
  return (
    <CollapsibleCard toolName={toolName}>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap text-[11px] text-espresso">
        {JSON.stringify(result, null, 2).slice(0, 800)}
      </pre>
    </CollapsibleCard>
  );
}
